// src/app/shared/notifications/notifications-page/notifications-page.component.ts

import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subject, filter, takeUntil } from 'rxjs';

import { NotificationsNavComponent } from '../../notifications-nav/notifications-nav.component';
import { RequiredNotificationsService } from '../notifications.service';
import {
  BookedHourNotificationDto,
  DayOffRequestGroupNotificationDto,
  UserNotificationsDto,
} from '../notifications.model';

type NotificationsPageTab = 'timeOff' | 'supervisorHours' | 'myHours';

interface BookedHourChangeModalVm {
  row: BookedHourNotificationDto;
  isSelfApproval: boolean;
  newBookedHours: number;
  bypassEmployeeApproval: boolean;
  errorMessage: string | null;
}

type ConfirmationAction =
  | { type: 'approveDayOff'; group: DayOffRequestGroupNotificationDto }
  | { type: 'rejectDayOff'; group: DayOffRequestGroupNotificationDto }
  | { type: 'approveSupervisorBookedHour'; row: BookedHourNotificationDto }
  | { type: 'rejectSupervisorBookedHour'; row: BookedHourNotificationDto }
  | { type: 'approveSelfBookedHour'; row: BookedHourNotificationDto }
  | { type: 'rejectSelfBookedHour'; row: BookedHourNotificationDto }
  | { type: 'submitBookedHourChange' };

@Component({
  selector: 'app-notifications-page',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe, DecimalPipe, NotificationsNavComponent],
  templateUrl: './notifications-page.component.html',
  styleUrls: ['./notifications-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsPageComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  readonly notifications$: Observable<UserNotificationsDto>;
  readonly isLoading$: Observable<boolean>;
  readonly isActionRunning$: Observable<boolean>;
  readonly hasBlockingNotifications$: Observable<boolean>;
  readonly errorMessage$: Observable<string | null>;
  readonly currentFullYearWeek$: Observable<string | null>;

  activeTab: NotificationsPageTab = 'timeOff';
  bookedHourChangeModal: BookedHourChangeModalVm | null = null;
  confirmationAction: ConfirmationAction | null = null;

  constructor(
    private readonly requiredNotificationsService: RequiredNotificationsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
  ) {
    this.notifications$ = this.requiredNotificationsService.notifications$;
    this.isLoading$ = this.requiredNotificationsService.isLoading$;
    this.isActionRunning$ = this.requiredNotificationsService.isActionRunning$;
    this.hasBlockingNotifications$ = this.requiredNotificationsService.hasBlockingNotifications$;
    this.errorMessage$ = this.requiredNotificationsService.errorMessage$;
    this.currentFullYearWeek$ = this.requiredNotificationsService.currentFullYearWeek$;
  }

  ngOnInit(): void {
    this.setActiveTabFromUrl(this.router.url);
    this.requiredNotificationsService.loadRequiredNotifications();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => this.setActiveTabFromUrl(event.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  refresh(): void {
    this.requiredNotificationsService.loadRequiredNotifications();
  }

  approveAll(): void {
    this.requiredNotificationsService.approveAllPending().subscribe();
  }

  getTotalCount(notifications: UserNotificationsDto | null | undefined): number {
    return this.requiredNotificationsService.getTotalNotificationCount(notifications);
  }

  getPendingActionCount(notifications: UserNotificationsDto | null | undefined): number {
    return this.requiredNotificationsService.getPendingActionCount(notifications);
  }

  getTimeOffCount(notifications: UserNotificationsDto | null | undefined): number {
    return notifications?.dayOffRequestGroupsNeedToApproveBySupervisor?.length ?? 0;
  }

  getSupervisorHoursCount(notifications: UserNotificationsDto | null | undefined): number {
    return notifications?.bookedHoursNeedToApproveBySupervisor?.length ?? 0;
  }

  getMyHoursCount(notifications: UserNotificationsDto | null | undefined): number {
    return notifications?.bookedHoursNeedToApproveBySelf?.length ?? 0;
  }

  hasAnyNotifications(notifications: UserNotificationsDto | null | undefined): boolean {
    return this.getTotalCount(notifications) > 0;
  }

  getEmployeeName(employee: { firstName?: string | null; lastName?: string | null } | null | undefined): string {
    return employee ? `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim() || '—' : '—';
  }

  getEmployeeNumber(employee: { employeeNumber?: string | null } | null | undefined): string {
    return employee?.employeeNumber ? `#${employee.employeeNumber}` : '#—';
  }

  getBookedHourCompany(row: BookedHourNotificationDto): string {
    return row.company?.name ?? row.employee?.company?.name ?? '—';
  }

  getBookedHourDepartment(row: BookedHourNotificationDto): string {
    const department = row.department ?? row.employee?.department;

    if (!department) {
      return '—';
    }

    return `${department.code} - ${department.name}`;
  }

  getBookedHourWorkerType(row: BookedHourNotificationDto): string {
    return row.workerType?.name ?? '—';
  }

  getBookedHourYyww(row: BookedHourNotificationDto): string {
    return row.yyww?.fullYearWeek ?? row.yyww?.name ?? '—';
  }

  getBookedHourDetails(row: BookedHourNotificationDto): string {
    const values = [
      this.getBookedHourCompany(row),
      this.getBookedHourDepartment(row),
      this.getBookedHourWorkerType(row),
    ].filter((v) => v && v !== '—');

    return values.length ? values.join(' • ') : '—';
  }

  getDayOffTotalHours(group: DayOffRequestGroupNotificationDto): number {
    return (group.dayOffRequestDates ?? []).reduce(
      (total: number, day) => total + Number(day.totalHours ?? 0),
      0,
    );
  }

  getDayOffDatesLabel(group: DayOffRequestGroupNotificationDto): string {
    const dates = (group.dayOffRequestDates ?? [])
      .map((day) => day.date)
      .filter((date): date is string => !!date);

    return dates.length ? dates.join(', ') : '—';
  }

  isDayOffActionable(group: DayOffRequestGroupNotificationDto): boolean {
    return group.isApproved === null;
  }

  approveDayOff(group: DayOffRequestGroupNotificationDto): void {
    this.confirmationAction = { type: 'approveDayOff', group };
    this.cdr.markForCheck();
  }

  rejectDayOff(group: DayOffRequestGroupNotificationDto): void {
    this.confirmationAction = { type: 'rejectDayOff', group };
    this.cdr.markForCheck();
  }

  approveSupervisorBookedHour(row: BookedHourNotificationDto): void {
    this.confirmationAction = { type: 'approveSupervisorBookedHour', row };
    this.cdr.markForCheck();
  }

  rejectSupervisorBookedHour(row: BookedHourNotificationDto): void {
    this.confirmationAction = { type: 'rejectSupervisorBookedHour', row };
    this.cdr.markForCheck();
  }

  openSupervisorBookedHourChange(row: BookedHourNotificationDto): void {
    this.openBookedHourChange(row, false);
  }

  openSelfBookedHourChange(row: BookedHourNotificationDto): void {
    this.openBookedHourChange(row, true);
  }

  approveSelfBookedHour(row: BookedHourNotificationDto): void {
    this.confirmationAction = { type: 'approveSelfBookedHour', row };
    this.cdr.markForCheck();
  }

  rejectSelfBookedHour(row: BookedHourNotificationDto): void {
    this.confirmationAction = { type: 'rejectSelfBookedHour', row };
    this.cdr.markForCheck();
  }

  closeBookedHourChangeModal(): void {
    this.bookedHourChangeModal = null;
    this.cdr.markForCheck();
  }

  onChangeHoursInput(value: number | string): void {
    if (!this.bookedHourChangeModal) return;

    const next = Number(value);

    this.bookedHourChangeModal = {
      ...this.bookedHourChangeModal,
      newBookedHours: Number.isFinite(next) ? Math.max(0, next) : 0,
      errorMessage: null,
    };

    this.cdr.markForCheck();
  }

  onBypassEmployeeApprovalChange(value: boolean): void {
    if (!this.bookedHourChangeModal) return;

    this.bookedHourChangeModal = {
      ...this.bookedHourChangeModal,
      bypassEmployeeApproval: !!value,
    };

    this.cdr.markForCheck();
  }

  submitBookedHourChange(): void {
    if (!this.bookedHourChangeModal) return;

    const modal = this.bookedHourChangeModal;
    const cleanHours = this.round2(Number(modal.newBookedHours));

    if (!Number.isFinite(cleanHours) || cleanHours < 0) {
      this.setBookedHourChangeError('Hours must be zero or greater.');
      return;
    }

    if (this.isMatch(cleanHours, Number(modal.row.hours) || 0)) {
      this.setBookedHourChangeError('Enter a different hour value before saving.');
      return;
    }

    this.confirmationAction = { type: 'submitBookedHourChange' };
    this.cdr.markForCheck();
  }

  cancelConfirmation(): void {
    this.confirmationAction = null;
    this.cdr.markForCheck();
  }

  confirmAction(): void {
    if (!this.confirmationAction) return;

    const action = this.confirmationAction;
    this.confirmationAction = null;

    switch (action.type) {
      case 'approveDayOff':
        this.requiredNotificationsService.approveDayOffRequest(action.group.id);
        break;
      case 'rejectDayOff':
        this.requiredNotificationsService.rejectDayOffRequest(action.group.id);
        break;
      case 'approveSupervisorBookedHour':
        this.requiredNotificationsService.approveBookedHour(action.row.id);
        break;
      case 'rejectSupervisorBookedHour':
        this.requiredNotificationsService.rejectBookedHour(action.row.id);
        break;
      case 'approveSelfBookedHour':
        this.requiredNotificationsService.approveSelfBookedHour(action.row.id);
        break;
      case 'rejectSelfBookedHour':
        this.requiredNotificationsService.rejectSelfBookedHour(action.row.id);
        break;
      case 'submitBookedHourChange':
        this.confirmBookedHourChange();
        break;
    }

    this.cdr.markForCheck();
  }

  getConfirmationTitle(): string {
    switch (this.confirmationAction?.type) {
      case 'approveDayOff':
      case 'approveSupervisorBookedHour':
      case 'approveSelfBookedHour':
        return 'Approve Notification';
      case 'rejectDayOff':
      case 'rejectSupervisorBookedHour':
      case 'rejectSelfBookedHour':
        return 'Reject Notification';
      case 'submitBookedHourChange':
        return 'Confirm Hour Change';
      default:
        return 'Confirm Action';
    }
  }

  getConfirmationMessage(): string {
    switch (this.confirmationAction?.type) {
      case 'approveDayOff':
        return 'Are you sure you want to approve this time off request?';
      case 'rejectDayOff':
        return 'Are you sure you want to reject this time off request?';
      case 'approveSupervisorBookedHour':
      case 'approveSelfBookedHour':
        return 'Are you sure you want to approve these booked hours?';
      case 'rejectSupervisorBookedHour':
      case 'rejectSelfBookedHour':
        return 'Are you sure you want to reject these booked hours?';
      case 'submitBookedHourChange':
        return 'Are you sure you want to save this changed hour value?';
      default:
        return 'Are you sure you want to continue?';
    }
  }

  getConfirmationButtonText(): string {
    switch (this.confirmationAction?.type) {
      case 'approveDayOff':
      case 'approveSupervisorBookedHour':
      case 'approveSelfBookedHour':
        return 'Approve';
      case 'rejectDayOff':
      case 'rejectSupervisorBookedHour':
      case 'rejectSelfBookedHour':
        return 'Reject';
      case 'submitBookedHourChange':
        return 'Save Change';
      default:
        return 'Confirm';
    }
  }

  isConfirmationDanger(): boolean {
    return (
      this.confirmationAction?.type === 'rejectDayOff' ||
      this.confirmationAction?.type === 'rejectSupervisorBookedHour' ||
      this.confirmationAction?.type === 'rejectSelfBookedHour'
    );
  }

  trackById(_: number, item: { id: number }): number {
    return item.id;
  }

  private setActiveTabFromUrl(url: string): void {
    const cleanUrl = String(url).split('?')[0].split('#')[0];

    if (cleanUrl.endsWith('/supervisor-hours')) {
      this.activeTab = 'supervisorHours';
    } else if (cleanUrl.endsWith('/my-hours')) {
      this.activeTab = 'myHours';
    } else {
      this.activeTab = 'timeOff';
    }

    this.cdr.markForCheck();
  }

  private openBookedHourChange(row: BookedHourNotificationDto, isSelfApproval: boolean): void {
    this.bookedHourChangeModal = {
      row,
      isSelfApproval,
      newBookedHours: this.round2(Number(row.hours) || 0),
      bypassEmployeeApproval: false,
      errorMessage: null,
    };

    this.cdr.markForCheck();
  }

  private confirmBookedHourChange(): void {
    if (!this.bookedHourChangeModal) return;

    const modal = this.bookedHourChangeModal;
    const cleanHours = this.round2(Number(modal.newBookedHours));

    if (modal.isSelfApproval) {
      this.requiredNotificationsService.requestSelfBookedHourChange(modal.row.id, cleanHours);
      this.closeBookedHourChangeModal();
      return;
    }

    if (modal.bypassEmployeeApproval) {
      this.requiredNotificationsService.approveBookedHourWithChange(modal.row.id, cleanHours);
    } else {
      this.requiredNotificationsService.requestBookedHourChange(modal.row.id, cleanHours);
    }

    this.closeBookedHourChangeModal();
  }

  private setBookedHourChangeError(message: string): void {
    if (!this.bookedHourChangeModal) return;

    this.bookedHourChangeModal = {
      ...this.bookedHourChangeModal,
      errorMessage: message,
    };

    this.cdr.markForCheck();
  }

  private isMatch(a: number, b: number): boolean {
    return Math.abs(this.round2(a) - this.round2(b)) <= 0.01;
  }

  private round2(value: number): number {
    return Number.isFinite(value) ? Math.round((value + Number.EPSILON) * 100) / 100 : 0;
  }
}